import {
  ExclamationTriangleIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { ServiceTitanInvoiceLink } from "@/core/components/service-titan-invoice-link";
import { ServiceTitanJobLink } from "@/core/components/service-titan-job-link";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatMoney } from "@/core/utils/money";
import { COMMISSION_TRACE_KEY } from "@/icm/constants";
import { useGetIcmCellOverrides } from "@/icm/query";
import type { IcmCellOverride, IcmLineItem } from "@/icm/types";
import { formatTraceValue, humanizeKey } from "@/icm/utils";
import { EditCellModal } from "./edit-cell-modal";

interface IcmLineItemSidebarProps {
  lineItem: IcmLineItem;
  onClose: () => void;
  businessId?: string | null;
  statementId?: string;
}

interface TraceEntry {
  key: string;
  label: string;
  rawValue: unknown;
  displayValue: unknown;
  override: IcmCellOverride | null;
}

export const IcmLineItemSidebar = ({
  lineItem,
  onClose,
  businessId,
  statementId,
}: IcmLineItemSidebarProps) => {
  const { data: overridesData } = useGetIcmCellOverrides(
    businessId,
    statementId,
  );

  const overrideMap = useMemo(
    () =>
      new Map(
        (overridesData ?? [])
          .filter(
            (o) =>
              o.current && o.source_record_id === lineItem.source_record_id,
          )
          .map((o) => [o.column_name, o] as const),
      ),
    [overridesData, lineItem.source_record_id],
  );

  const [editingCell, setEditingCell] = useState<{
    columnName: string;
    currentValue: unknown;
    existingOverride: IcmCellOverride | null;
  } | null>(null);

  const payoutOverride = overrideMap.get(COMMISSION_TRACE_KEY) ?? null;
  const traceEntries = useMemo<TraceEntry[]>(
    () =>
      Object.entries(lineItem.calculation_trace)
        .filter(([key]) => key.toLowerCase() !== COMMISSION_TRACE_KEY)
        .map(([key, rawValue]) => {
          const override = overrideMap.get(key) ?? null;

          return {
            key,
            label: humanizeKey(key),
            rawValue,
            displayValue: override ? override.value : rawValue,
            override,
          };
        }),
    [lineItem.calculation_trace, overrideMap],
  );

  return (
    <Stack fullWidth gap="none" className="h-full">
      <Stack gap="lg" className="bg-bg-secondary/50 p-6" fullWidth>
        <Stack direction="row" justify="between" align="start" fullWidth>
          <Stack gap="xs" className="py-5">
            <Typography variant="titleW3">
              {lineItem.payout_rule_name}
            </Typography>
            {lineItem.customer_name && (
              <Typography variant="bodyW2" textColor="secondary">
                {lineItem.customer_name}
              </Typography>
            )}
            {lineItem.job_id ? (
              <ServiceTitanJobLink jobId={lineItem.job_id} />
            ) : (
              lineItem.invoice_id && (
                <ServiceTitanInvoiceLink invoiceId={lineItem.invoice_id} />
              )
            )}
          </Stack>
          <XMarkIcon
            className="h-6 w-6 shrink-0 cursor-pointer"
            onClick={onClose}
          />
        </Stack>

        <Stack gap="md" fullWidth>
          <Stack direction="row" gap="xl" fullWidth>
            {lineItem.job_completed_on && (
              <Stack gap="xs" className="w-[50%]">
                <Typography variant="subtitleW3" textColor="tertiary">
                  Completed
                </Typography>
                <Typography variant="bodyW2">
                  {dayjs(lineItem.job_completed_on).format("MMM DD, YYYY")}
                </Typography>
              </Stack>
            )}
            <Stack gap="xs" className="w-[50%]">
              <Typography variant="subtitleW3" textColor="tertiary">
                Payout
              </Typography>
              <Stack direction="row" gap="sm" align="center" className="group">
                <Typography variant="bodyW2">
                  {formatMoney(
                    String(payoutOverride?.value ?? lineItem.payout_amount),
                  )}
                </Typography>
                {payoutOverride && (
                  <Badge variant="blue" size="sm">
                    Edited
                  </Badge>
                )}
                <Button
                  type="button"
                  variant="text"
                  size="iconSm"
                  className="pointer-events-none text-tertiary-text opacity-0 transition-opacity focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
                  onClick={() =>
                    setEditingCell({
                      columnName: COMMISSION_TRACE_KEY,
                      currentValue: lineItem.payout_amount,
                      existingOverride: payoutOverride,
                    })
                  }
                  aria-label="Edit payout"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
              </Stack>
            </Stack>
          </Stack>
          {lineItem.business_unit_name && (
            <Stack gap="xs">
              <Typography variant="subtitleW3" textColor="tertiary">
                Business Unit
              </Typography>
              <Typography variant="bodyW2">
                {lineItem.business_unit_name}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Separator />

      <ScrollArea className="w-full flex-1">
        <Stack gap="none" fullWidth className="px-6 py-2">
          {lineItem.warnings?.length > 0 && (
            <Stack gap="sm" fullWidth className="pb-2 pt-4">
              {lineItem.warnings.map((msg, i) => (
                <FeedbackBanner key={i} variant="warning" iconSize="sm">
                  <Typography variant="bodyW2">{msg}</Typography>
                </FeedbackBanner>
              ))}
            </Stack>
          )}
          {traceEntries.map((entry) => (
            <Stack key={entry.key} gap="none" fullWidth>
              <Stack
                direction="row"
                justify="between"
                align="center"
                fullWidth
                className="group py-3"
              >
                <Stack direction="row" gap="sm" align="center">
                  <Typography variant="bodyW2" textColor="secondary">
                    {entry.label}
                  </Typography>
                  {entry.override && (
                    <Badge variant="blue" size="sm">
                      Edited
                    </Badge>
                  )}
                </Stack>
                <Stack direction="row" gap="sm" align="center">
                  {entry.override?.stale && (
                    <ExclamationTriangleIcon
                      className="h-4 w-4 text-gold-50"
                      title="Override may be stale"
                    />
                  )}
                  <Typography variant="bodyW2">
                    {formatTraceValue(entry.key, entry.displayValue)}
                  </Typography>
                  <Button
                    type="button"
                    variant="text"
                    size="iconSm"
                    className="pointer-events-none text-tertiary-text opacity-0 transition-opacity focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
                    onClick={() =>
                      setEditingCell({
                        columnName: entry.key,
                        currentValue: entry.rawValue,
                        existingOverride: entry.override,
                      })
                    }
                    aria-label={`Edit ${entry.label}`}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                </Stack>
              </Stack>
              <Separator />
            </Stack>
          ))}
        </Stack>
      </ScrollArea>

      <EditCellModal
        open={!!editingCell}
        onOpenChange={(open) => !open && setEditingCell(null)}
        businessId={businessId}
        statementId={statementId}
        lineItem={lineItem}
        columnName={editingCell?.columnName ?? ""}
        currentValue={editingCell?.currentValue}
        existingOverride={editingCell?.existingOverride ?? null}
      />
    </Stack>
  );
};
